import React from "react";
import { TrendingUp } from "lucide-react";
import { Badge } from "../../../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  clusterTitle,
  formatCount,
  formatDateTime,
  formatMix,
  isRetrievalBug,
  lifeAreaLabel,
  statusMeta,
} from "./researchPulseFormat";

export const ClusterStatusBadge = ({ status, className }) => {
  const meta = statusMeta(status);
  return (
    <Badge variant={meta.variant} className={className} title={meta.hint}>
      {meta.label}
    </Badge>
  );
};

/** The "not a research gap" call-out for RETRIEVAL_BUG rows. */
export const RetrievalBugNote = ({ className = "" }) => (
  <span className={`text-xs font-medium text-destructive ${className}`}>
    Retrieval bug — route to QA
  </span>
);

/** One table of cluster rows; used by the list and by each digest section. */
const ClusterTable = ({ rows, onOpen }) => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Topic / situation</TableHead>
          <TableHead>Life areas</TableHead>
          <TableHead className="text-right">Frequency</TableHead>
          <TableHead className="text-right">Members</TableHead>
          <TableHead>Trend</TableHead>
          <TableHead>Sources</TableHead>
          <TableHead>Verdicts</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Last seen</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const bug = isRetrievalBug(row);
          const open = () => onOpen(row.cluster_id);
          return (
            <TableRow
              key={row.cluster_id}
              data-testid={`cluster-row-${row.cluster_id}`}
              className={`cursor-pointer ${bug ? "bg-destructive/5 hover:bg-destructive/10" : ""}`}
              tabIndex={0}
              onClick={open}
              onKeyDown={(e) => {
                if (e.key === "Enter") open();
              }}
            >
              <TableCell className="min-w-[220px]">
                <div className="font-medium">{clusterTitle(row)}</div>
                {row.situation_family && row.topic && (
                  <div className="text-xs text-muted-foreground">{row.situation_family}</div>
                )}
                {bug && <RetrievalBugNote className="block" />}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {(row.life_areas || []).map(lifeAreaLabel).join(", ") || "—"}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCount(row.frequency)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCount(row.distinct_members)}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {row.trend?.rising ? (
                  <span
                    className="inline-flex items-center text-green-600"
                    title={`Velocity ${row.trend?.velocity ?? 0}`}
                  >
                    <TrendingUp className="mr-1 h-4 w-4" aria-label="Rising" />
                    {row.trend?.last_window ?? 0}
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {row.trend?.last_window ?? "—"}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-xs">{formatMix(row.sources) || "—"}</TableCell>
              <TableCell className="text-xs">{formatMix(row.verdicts) || "—"}</TableCell>
              <TableCell>
                <ClusterStatusBadge status={row.status} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs">
                {formatDateTime(row.last_seen_at)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

export default ClusterTable;
